// client/src/components/shared/orders/Orders.jsx

import React, {
  useState,
} from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import {
  ArrowBackOutlined,
  ContentCopyOutlined,
  LocalShippingOutlined,
  OpenInNewOutlined,
  ReceiptLongOutlined,
} from '@mui/icons-material';

import {
  Link as RouterLink,
} from 'react-router-dom';

import {
  tokens,
} from '../../../theme';

import Navbar
  from '../../react-public/global/Navbar';

import Footer
  from '../../react-public/global/Footer';

import {
  useGetMyOrdersQuery,
} from '../../../features/orders/ordersApiSlice';


const siteFont =
  'Poppins, sans-serif';


const formatCurrency =
  (
    cents,
    currency = 'usd'
  ) =>
    new Intl
      .NumberFormat(
        'en-US',
        {
          style:
            'currency',

          currency:
            String(
              currency
            ).toUpperCase(),
        }
      )
      .format(
        Number(
          cents || 0
        ) / 100
      );


const formatStatus =
  (status) =>
    String(
      status ||
      ''
    )
      .replaceAll(
        '_',
        ' '
      )
      .replace(
        /\b\w/g,
        (character) =>
          character.toUpperCase()
      );


const Orders =
  () => {
    const theme =
      useTheme();

    const colors =
      tokens(
        theme.palette.mode
      );


    const {
      data,
      isLoading,
      isError,
      error,
    } =
      useGetMyOrdersQuery();


    const orders =
      data?.orders ||
      [];


    const [
      copiedOrderId,
      setCopiedOrderId,
    ] =
      useState(
        null
      );


    const [
      snackbar,
      setSnackbar,
    ] =
      useState({
        open:
          false,

        message:
          '',

        severity:
          'success',
      });


    const showMessage =
      (
        message,
        severity =
          'success'
      ) => {
        setSnackbar({
          open:
            true,

          message,

          severity,
        });
      };


    const handleCopyOrderLink =
      async (
        order
      ) => {
        if (
          !order
            ?.orderStatusUrl
        ) {
          showMessage(
            'Order status link is unavailable.',
            'error'
          );

          return;
        }

        try {
          await navigator
            .clipboard
            .writeText(
              order
                .orderStatusUrl
            );

          setCopiedOrderId(
            order._id
          );

          showMessage(
            'Order status link copied.'
          );

          window.setTimeout(
            () => {
              setCopiedOrderId(
                null
              );
            },
            1800
          );
        } catch (
        error
        ) {
          console.error(
            'Unable to copy order link:',
            error
          );

          showMessage(
            'Unable to copy order status link.',
            'error'
          );
        }
      };


    return (
      <Box
        width="100%"
        minHeight="100vh"
        sx={{
          background:
            'radial-gradient(circle at 50% 15%, rgba(0,255,170,0.11), transparent 32%)',
        }}
      >
        <Navbar />


        <Container
          maxWidth="md"
          sx={{
            py: {
              xs:
                6,

              md:
                10,
            },

            fontFamily:
              siteFont,

            '& .MuiTypography-root': {
              fontFamily:
                siteFont,
            },

            '& .MuiButton-root': {
              fontFamily:
                siteFont,
            },
          }}
        >
          {/* BACK TO CONSOLE */}

          <Box
            sx={{
              mb: 2,
            }}
          >
            <Button
              component={RouterLink}
              to="/console"
              startIcon={
                <ArrowBackOutlined />
              }
              sx={{
                color:
                  'rgba(255,255,255,0.78)',

                border:
                  '1px solid rgba(255,255,255,0.14)',

                backgroundColor:
                  'rgba(255,255,255,0.04)',

                borderRadius:
                  '999px',

                px:
                  2.25,

                py:
                  1,

                fontWeight:
                  800,

                '&:hover': {
                  color:
                    'white',

                  borderColor:
                    'rgba(0,255,170,0.32)',

                  backgroundColor:
                    'rgba(0,255,170,0.07)',
                },
              }}
            >
              Back to ATG Console
            </Button>
          </Box>
          <Paper
            elevation={
              0
            }
            sx={{
              width:
                '100%',

              boxSizing:
                'border-box',

              p: {
                xs:
                  3,

                sm:
                  4,

                md:
                  5,
              },

              borderRadius:
                '28px',

              color:
                'white',

              backgroundColor:
                'rgba(18,24,32,0.84)',

              backdropFilter:
                'blur(18px)',

              border:
                '1px solid rgba(255,255,255,0.11)',

              boxShadow:
                '0 30px 90px rgba(0,0,0,0.30)',
            }}
          >
            {/* HEADER */}

            <Box
              textAlign="center"
            >
              <Box
                sx={{
                  width:
                    82,

                  height:
                    82,

                  mx:
                    'auto',

                  borderRadius:
                    '50%',

                  display:
                    'flex',

                  alignItems:
                    'center',

                  justifyContent:
                    'center',

                  backgroundColor:
                    'rgba(0,255,170,0.12)',

                  border:
                    '1px solid rgba(0,255,170,0.30)',
                }}
              >
                <ReceiptLongOutlined
                  sx={{
                    fontSize:
                      46,

                    color:
                      colors
                        .greenAccent[400],
                  }}
                />
              </Box>


              <Typography
                variant="h2"
                sx={{
                  mt:
                    3,

                  color:
                    'white',

                  fontWeight:
                    900,

                  letterSpacing:
                    '-0.055em',

                  lineHeight:
                    1,
                }}
              >
                My Orders
              </Typography>


              <Typography
                sx={{
                  mt:
                    1.5,

                  mx:
                    'auto',

                  maxWidth:
                    '650px',

                  color:
                    'rgba(255,255,255,0.72)',

                  lineHeight:
                    1.7,

                  fontSize:
                    '1rem',
                }}
              >
                View your purchases,
                fulfillment status,
                shipping information,
                and tracking.
              </Typography>
            </Box>


            <Divider
              sx={{
                my:
                  4,

                borderColor:
                  'rgba(255,255,255,0.10)',
              }}
            />


            {/* LOADING */}

            {isLoading && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py={
                  8
                }
              >
                <CircularProgress
                  sx={{
                    color:
                      colors
                        .greenAccent[400],
                  }}
                />

                <Typography
                  sx={{
                    mt:
                      2,

                    color:
                      'rgba(255,255,255,0.72)',

                    fontWeight:
                      700,
                  }}
                >
                  Loading your orders...
                </Typography>
              </Box>
            )}


            {/* ERROR */}

            {isError && (
              <Alert
                severity="error"
              >
                {error
                  ?.data
                  ?.message ||
                  'Unable to load your orders.'}
              </Alert>
            )}


            {/* EMPTY */}

            {!isLoading &&
              !isError &&
              orders.length ===
              0 && (
                <Box
                  sx={{
                    p:
                      4,

                    textAlign:
                      'center',

                    borderRadius:
                      '20px',

                    backgroundColor:
                      'rgba(255,255,255,0.04)',

                    border:
                      '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <ReceiptLongOutlined
                    sx={{
                      fontSize:
                        48,

                      color:
                        'rgba(255,255,255,0.35)',
                    }}
                  />

                  <Typography
                    variant="h4"
                    sx={{
                      mt:
                        2,

                      color:
                        'white',

                      fontWeight:
                        900,
                    }}
                  >
                    No orders yet
                  </Typography>

                  <Typography
                    sx={{
                      mt:
                        1,

                      color:
                        'rgba(255,255,255,0.60)',
                    }}
                  >
                    Your USFuel purchases
                    will appear here.
                  </Typography>
                </Box>
              )}


            {/* ORDERS */}

            {!isLoading &&
              !isError &&
              orders.length >
              0 && (
                <Stack
                  spacing={
                    3
                  }
                >
                  {orders.map(
                    (
                      order
                    ) => {
                      const fulfillment =
                        order
                          .fulfillment ||
                        {};

                      const fulfillmentStatus =
                        fulfillment
                          .status ||
                        'unfulfilled';

                      const isCopied =
                        copiedOrderId ===
                        order._id;


                      return (
                        <Paper
                          key={
                            order._id
                          }
                          elevation={
                            0
                          }
                          sx={{
                            p: {
                              xs:
                                2.5,

                              sm:
                                3,
                            },

                            borderRadius:
                              '22px',

                            color:
                              'white',

                            backgroundColor:
                              'rgba(0,0,0,0.18)',

                            border:
                              '1px solid rgba(255,255,255,0.10)',
                          }}
                        >
                          {/* ORDER HEADER */}

                          <Stack
                            direction={{
                              xs:
                                'column',

                              sm:
                                'row',
                            }}
                            justifyContent="space-between"
                            alignItems={{
                              xs:
                                'flex-start',

                              sm:
                                'center',
                            }}
                            spacing={
                              2
                            }
                          >
                            <Box>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={
                                  1.25
                                }
                              >
                                <ReceiptLongOutlined
                                  sx={{
                                    color:
                                      colors
                                        .greenAccent[400],
                                  }}
                                />

                                <Typography
                                  variant="h4"
                                  sx={{
                                    color:
                                      'white',

                                    fontWeight:
                                      900,
                                  }}
                                >
                                  Order{' '}
                                  {String(
                                    order._id
                                  )
                                    .slice(
                                      -8
                                    )
                                    .toUpperCase()}
                                </Typography>
                              </Stack>


                              <Typography
                                sx={{
                                  mt:
                                    0.75,

                                  color:
                                    'rgba(255,255,255,0.55)',

                                  fontSize:
                                    '0.9rem',
                                }}
                              >
                                {new Date(
                                  order.createdAt
                                ).toLocaleDateString(
                                  'en-US',
                                  {
                                    year:
                                      'numeric',

                                    month:
                                      'long',

                                    day:
                                      'numeric',
                                  }
                                )}
                              </Typography>
                            </Box>


                            <Stack
                              direction="row"
                              spacing={
                                1
                              }
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Chip
                                size="small"
                                label={
                                  formatStatus(
                                    order.status
                                  )
                                }
                                sx={{
                                  fontWeight:
                                    800,

                                  color:
                                    order.status ===
                                      'paid'
                                      ? colors
                                        .greenAccent[300]
                                      : 'white',

                                  backgroundColor:
                                    order.status ===
                                      'paid'
                                      ? 'rgba(0,255,170,0.12)'
                                      : 'rgba(255,255,255,0.08)',

                                  border:
                                    order.status ===
                                      'paid'
                                      ? '1px solid rgba(0,255,170,0.25)'
                                      : '1px solid rgba(255,255,255,0.12)',
                                }}
                              />


                              <Chip
                                size="small"
                                icon={
                                  <LocalShippingOutlined />
                                }
                                label={
                                  formatStatus(
                                    fulfillmentStatus
                                  )
                                }
                                sx={{
                                  fontWeight:
                                    800,

                                  color:
                                    fulfillmentStatus ===
                                      'delivered'
                                      ? colors
                                        .greenAccent[300]
                                      : 'white',

                                  backgroundColor:
                                    fulfillmentStatus ===
                                      'delivered'
                                      ? 'rgba(0,255,170,0.12)'
                                      : fulfillmentStatus ===
                                        'shipped'
                                        ? 'rgba(80,150,255,0.16)'
                                        : 'rgba(255,255,255,0.08)',

                                  border:
                                    fulfillmentStatus ===
                                      'delivered'
                                      ? '1px solid rgba(0,255,170,0.25)'
                                      : '1px solid rgba(255,255,255,0.12)',

                                  '& .MuiChip-icon': {
                                    color:
                                      'inherit',
                                  },
                                }}
                              />
                            </Stack>
                          </Stack>


                          <Divider
                            sx={{
                              my:
                                3,

                              borderColor:
                                'rgba(255,255,255,0.09)',
                            }}
                          />


                          {/* ITEMS */}

                          <Stack
                            spacing={
                              0
                            }
                          >
                            {(
                              order.items ||
                              []
                            ).map(
                              (
                                item,
                                index
                              ) => (
                                <Box
                                  key={
                                    item._id ||
                                    `${order._id}-${index}`
                                  }
                                  sx={{
                                    py:
                                      1.5,

                                    borderBottom:
                                      index <
                                        order.items
                                          .length -
                                        1
                                        ? '1px solid rgba(255,255,255,0.07)'
                                        : 'none',
                                  }}
                                >
                                  <Stack
                                    direction={{
                                      xs:
                                        'column',

                                      sm:
                                        'row',
                                    }}
                                    justifyContent="space-between"
                                    spacing={
                                      1
                                    }
                                  >
                                    <Box>
                                      <Typography
                                        sx={{
                                          color:
                                            'white',

                                          fontWeight:
                                            800,
                                        }}
                                      >
                                        {item.quantity}
                                        {' × '}
                                        {item.name}
                                      </Typography>


                                      <Typography
                                        sx={{
                                          mt:
                                            0.35,

                                          color:
                                            'rgba(255,255,255,0.50)',

                                          fontSize:
                                            '0.85rem',
                                        }}
                                      >
                                        {formatCurrency(
                                          item.unitAmountCents,
                                          order.currency
                                        )}{' '}
                                        each
                                      </Typography>
                                    </Box>


                                    <Typography
                                      sx={{
                                        color:
                                          'white',

                                        fontWeight:
                                          800,

                                        whiteSpace:
                                          'nowrap',
                                      }}
                                    >
                                      {formatCurrency(
                                        item.unitAmountCents *
                                        item.quantity,
                                        order.currency
                                      )}
                                    </Typography>
                                  </Stack>


                                  {Array.isArray(
                                    item
                                      .fitmentSelections
                                  ) &&
                                    item
                                      .fitmentSelections
                                      .length >
                                    0 && (
                                      <Box
                                        sx={{
                                          mt:
                                            1.25,

                                          pl:
                                            2,

                                          borderLeft:
                                            '2px solid rgba(0,255,170,0.24)',
                                        }}
                                      >
                                        {item
                                          .fitmentSelections
                                          .map(
                                            (
                                              fitment,
                                              fitmentIndex
                                            ) => (
                                              <Typography
                                                key={
                                                  fitmentIndex
                                                }
                                                sx={{
                                                  color:
                                                    'rgba(255,255,255,0.58)',

                                                  fontSize:
                                                    '0.85rem',
                                                }}
                                              >
                                                {fitment.label ||
                                                  fitment.atgModel}
                                                {' × '}
                                                {fitment.quantity}
                                              </Typography>
                                            )
                                          )}
                                      </Box>
                                    )}
                                </Box>
                              )
                            )}
                          </Stack>


                          <Divider
                            sx={{
                              my:
                                3,

                              borderColor:
                                'rgba(255,255,255,0.09)',
                            }}
                          />


                          {/* SHIPPING + TOTAL */}

                          <Stack
                            direction={{
                              xs:
                                'column',

                              md:
                                'row',
                            }}
                            justifyContent="space-between"
                            spacing={
                              3
                            }
                          >
                            <Box
                              sx={{
                                flex:
                                  1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color:
                                    'rgba(255,255,255,0.52)',

                                  fontSize:
                                    '0.75rem',

                                  fontWeight:
                                    800,

                                  textTransform:
                                    'uppercase',

                                  letterSpacing:
                                    '0.08em',
                                }}
                              >
                                Ship To
                              </Typography>


                              <Typography
                                sx={{
                                  mt:
                                    0.75,

                                  color:
                                    'white',

                                  fontWeight:
                                    800,
                                }}
                              >
                                {order
                                  .shippingAddress
                                  ?.firstName}{' '}
                                {order
                                  .shippingAddress
                                  ?.lastName}
                              </Typography>


                              {order
                                .shippingAddress
                                ?.street1 && (
                                  <Typography
                                    sx={{
                                      mt:
                                        0.3,

                                      color:
                                        'rgba(255,255,255,0.68)',
                                    }}
                                  >
                                    {
                                      order
                                        .shippingAddress
                                        .street1
                                    }
                                  </Typography>
                                )}


                              {order
                                .shippingAddress
                                ?.street2 && (
                                  <Typography
                                    sx={{
                                      color:
                                        'rgba(255,255,255,0.68)',
                                    }}
                                  >
                                    {
                                      order
                                        .shippingAddress
                                        .street2
                                    }
                                  </Typography>
                                )}


                              <Typography
                                sx={{
                                  color:
                                    'rgba(255,255,255,0.68)',
                                }}
                              >
                                {order
                                  .shippingAddress
                                  ?.city}

                                {order
                                  .shippingAddress
                                  ?.state
                                  ? `, ${order.shippingAddress.state}`
                                  : ''}

                                {order
                                  .shippingAddress
                                  ?.zipCode
                                  ? ` ${order.shippingAddress.zipCode}`
                                  : ''}
                              </Typography>
                            </Box>


                            <Box
                              sx={{
                                minWidth: {
                                  md:
                                    220,
                                },

                                textAlign: {
                                  xs:
                                    'left',

                                  md:
                                    'right',
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  color:
                                    'rgba(255,255,255,0.52)',

                                  fontSize:
                                    '0.75rem',

                                  fontWeight:
                                    800,

                                  textTransform:
                                    'uppercase',

                                  letterSpacing:
                                    '0.08em',
                                }}
                              >
                                Total
                              </Typography>


                              <Typography
                                variant="h3"
                                sx={{
                                  mt:
                                    0.5,

                                  color:
                                    'white',

                                  fontWeight:
                                    900,
                                }}
                              >
                                {formatCurrency(
                                  order.amountTotalCents,
                                  order.currency
                                )}
                              </Typography>
                            </Box>
                          </Stack>


                          {/* TRACKING */}

                          {fulfillment
                            .trackingNumber && (
                              <Box
                                sx={{
                                  mt:
                                    3,

                                  p:
                                    2.5,

                                  borderRadius:
                                    '18px',

                                  backgroundColor:
                                    'rgba(0,255,170,0.055)',

                                  border:
                                    '1px solid rgba(0,255,170,0.16)',
                                }}
                              >
                                <Stack
                                  direction={{
                                    xs:
                                      'column',

                                    sm:
                                      'row',
                                  }}
                                  justifyContent="space-between"
                                  alignItems={{
                                    xs:
                                      'flex-start',

                                    sm:
                                      'center',
                                  }}
                                  spacing={
                                    2
                                  }
                                >
                                  <Box>
                                    <Typography
                                      sx={{
                                        color:
                                          colors
                                            .greenAccent[300],

                                        fontWeight:
                                          900,
                                      }}
                                    >
                                      Shipment Tracking
                                    </Typography>


                                    <Typography
                                      sx={{
                                        mt:
                                          0.5,

                                        color:
                                          'rgba(255,255,255,0.58)',

                                        fontSize:
                                          '0.82rem',

                                        textTransform:
                                          'uppercase',

                                        letterSpacing:
                                          '0.06em',
                                      }}
                                    >
                                      {fulfillment
                                        .carrier ||
                                        'Carrier'}
                                    </Typography>


                                    <Typography
                                      sx={{
                                        mt:
                                          0.25,

                                        color:
                                          'white',

                                        fontWeight:
                                          800,

                                        wordBreak:
                                          'break-word',
                                      }}
                                    >
                                      {
                                        fulfillment
                                          .trackingNumber
                                      }
                                    </Typography>
                                  </Box>


                                  {fulfillment
                                    .trackingUrl && (
                                      <Button
                                        component="a"
                                        href={
                                          fulfillment
                                            .trackingUrl
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        endIcon={
                                          <OpenInNewOutlined />
                                        }
                                        sx={{
                                          flexShrink:
                                            0,

                                          backgroundColor:
                                            colors
                                              .greenAccent[400],

                                          color:
                                            '#06100c',

                                          borderRadius:
                                            '999px',

                                          px:
                                            3,

                                          py:
                                            1.3,

                                          fontWeight:
                                            900,

                                          boxShadow:
                                            '0 8px 24px rgba(0,255,170,0.14)',

                                          '&:hover': {
                                            backgroundColor:
                                              colors
                                                .greenAccent[300],
                                          },
                                        }}
                                      >
                                        Track Shipment
                                      </Button>
                                    )}
                                </Stack>
                              </Box>
                            )}


                          {/* ORDER ACTIONS */}

                          {order
                            .orderStatusUrl && (
                              <>
                                <Divider
                                  sx={{
                                    my:
                                      3,

                                    borderColor:
                                      'rgba(255,255,255,0.09)',
                                  }}
                                />


                                <Stack
                                  direction={{
                                    xs:
                                      'column',

                                    sm:
                                      'row',
                                  }}
                                  spacing={
                                    1.5
                                  }
                                >
                                  <Button
                                    component="a"
                                    href={
                                      order
                                        .orderStatusUrl
                                    }
                                    startIcon={
                                      <LocalShippingOutlined />
                                    }
                                    sx={{
                                      flex:
                                        1,

                                      backgroundColor:
                                        colors
                                          .greenAccent[400],

                                      color:
                                        '#06100c',

                                      borderRadius:
                                        '14px',

                                      py:
                                        1.4,

                                      fontWeight:
                                        900,

                                      '&:hover': {
                                        backgroundColor:
                                          colors
                                            .greenAccent[300],
                                      },
                                    }}
                                  >
                                    View Order Status
                                  </Button>


                                  <Button
                                    type="button"
                                    onClick={
                                      () =>
                                        handleCopyOrderLink(
                                          order
                                        )
                                    }
                                    startIcon={
                                      <ContentCopyOutlined />
                                    }
                                    sx={{
                                      flex:
                                        1,

                                      color:
                                        isCopied
                                          ? colors
                                            .greenAccent[300]
                                          : 'white',

                                      border:
                                        isCopied
                                          ? '1px solid rgba(0,255,170,0.36)'
                                          : '1px solid rgba(255,255,255,0.18)',

                                      backgroundColor:
                                        isCopied
                                          ? 'rgba(0,255,170,0.08)'
                                          : 'rgba(255,255,255,0.04)',

                                      borderRadius:
                                        '14px',

                                      py:
                                        1.4,

                                      fontWeight:
                                        800,

                                      '&:hover': {
                                        backgroundColor:
                                          'rgba(255,255,255,0.08)',
                                      },
                                    }}
                                  >
                                    {isCopied
                                      ? 'Copied!'
                                      : 'Copy Order Link'}
                                  </Button>
                                </Stack>
                              </>
                            )}
                        </Paper>
                      );
                    }
                  )}
                </Stack>
              )}
          </Paper>
        </Container>


        <Footer />


        <Snackbar
          open={
            snackbar.open
          }
          autoHideDuration={
            3000
          }
          onClose={
            () =>
              setSnackbar(
                (
                  previous
                ) => ({
                  ...previous,

                  open:
                    false,
                })
              )
          }
          anchorOrigin={{
            vertical:
              'bottom',

            horizontal:
              'center',
          }}
        >
          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
            onClose={
              () =>
                setSnackbar(
                  (
                    previous
                  ) => ({
                    ...previous,

                    open:
                      false,
                  })
                )
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  };


export default Orders;